import {
    MediaGuide,
    MediaUpdateGuide,
    MediaUpdateGuideProgressActionEnum,
    WorkflowWorkflowType,
} from '@calm/evexia-web-sdk';
import createMediaApi from '@rhg/libs/api/create-media-api';
import useAuth from '@rhg/libs/auth0/use-auth';
import {
    invalidateQueryKey,
    queryClient,
    useMutation,
} from '@rhg/libs/react-query';
import { getRhgAppHeader } from '@rhg/utils/browser';
import { getLocalDateTimeString } from '@rhg/utils/date-time';
import { MediaEvent } from '@rhg/utils/media';
import { getRemappedPackClass } from '@rhg/utils/programs';
import { useActiveCircle } from '../use-active-circle';
import { clinicalPacksQueryOptions } from '../use-clinical-packs';
import { programsQueryOptions } from '../use-programs';
import { guideQueryKey } from '../use-recipient-guide';
import { workflowNextStepQueryOptions } from '../use-workflow-next-step';
import {
    workflowByDefnIdForRecipientQueryOptions,
    workflowByTypeForRecipientQueryOptions,
} from '../use-workflows';

function getProgressAction(
    eventType: MediaEvent
): MediaUpdateGuideProgressActionEnum | undefined {
    switch (eventType) {
        case MediaEvent.Play:
            return 'play';
        case MediaEvent.Pause:
            return 'pause';
        case MediaEvent.Seeked:
            return 'seek';
        case MediaEvent.TimeUpdate:
            return 'complete_90';
        case MediaEvent.Ended:
            return 'complete';
        default:
            return undefined;
    }
}

interface UpdateGuideParameters {
    programId?: string;
    guideId?: string;
    packClass?: string;
    workflowDefnId?: string;
    workflowType?: WorkflowWorkflowType;
    request: MediaUpdateGuide;
}
export function useUpdateGuide() {
    const { getAccessTokenSilently } = useAuth();
    const { activeCircle, hasPermission } = useActiveCircle();
    const rHGApp = getRhgAppHeader();

    const { mutate, mutateAsync, ...otherProps } = useMutation({
        mutationFn: async ({
            programId = '',
            guideId = '',
            packClass = '',
            request,
        }: UpdateGuideParameters) => {
            const api = createMediaApi(getAccessTokenSilently);
            if (
                hasPermission('media', 'u') &&
                hasPermission('clinical_program', 'u')
            ) {
                const response = await api.updateGuide({
                    circleAuthorizationToken: activeCircle!.authorizationJWT!,
                    recipientId: activeCircle!.recipient.id,
                    _class: getRemappedPackClass(packClass),
                    programId,
                    guideId,
                    mediaUpdateGuide: request,
                    rHGApp,
                });
                return response;
            } else {
                throw new Error('Update Guide Permission Denied');
            }
        },
        onMutate: ({
            programId = '',
            guideId = '',
            packClass = '',
            request,
        }: UpdateGuideParameters) => {
            // Snapshot the previous value
            const key = guideQueryKey({ programId, packClass, guideId });
            const previous = queryClient.getQueryData<MediaGuide>(key);

            // Optimistically update to the new value
            if (previous) {
                queryClient.setQueryData<MediaGuide>(key, (previousGuide) =>
                    // This ternary is here to satisfy the linter, but it's not necessary because of the outer if statement
                    previousGuide
                        ? {
                              ...previousGuide,
                              ...(typeof request.progressDurationMs !==
                              'undefined'
                                  ? {
                                        progressDuration:
                                            request.progressDurationMs,
                                    }
                                  : {}),
                              ...(typeof request.isFavorite !== 'undefined'
                                  ? {
                                        favorite: request.isFavorite,
                                    }
                                  : {}),
                              ...(typeof request.timestamp !== 'undefined'
                                  ? { favoritedAt: request.timestamp }
                                  : {}),
                          }
                        : previous
                );
            }
            return { previous };
        },
        onSuccess: (
            _,
            {
                programId = '',
                workflowDefnId = '',
                workflowType = undefined,
                packClass = '',
            }
        ) => {
            if (activeCircle?.recipient.id) {
                if (packClass && programId) {
                    invalidateQueryKey(
                        programsQueryOptions.queryKey({
                            packClassesWithProgramIdsMap: {
                                [packClass]: new Set([programId]),
                            },
                            circleId: activeCircle.recipient.id,
                        })
                    );
                }

                if (workflowDefnId) {
                    invalidateQueryKey(
                        workflowNextStepQueryOptions.queryKey({
                            workflowDefnId,
                            recipientId: activeCircle.recipient.id,
                        })
                    );
                    invalidateQueryKey(
                        workflowByDefnIdForRecipientQueryOptions.queryKey({
                            workflowDefnId,
                            recipientId: activeCircle.recipient.id,
                        })
                    );
                    invalidateQueryKey(
                        clinicalPacksQueryOptions.queryKey({
                            packClasses: [packClass],
                            circleId: activeCircle.recipient.id,
                        })
                    );
                }
                if (workflowType) {
                    invalidateQueryKey(
                        workflowByTypeForRecipientQueryOptions.queryKey({
                            workflowType,
                            recipientId: activeCircle.recipient.id,
                        })
                    );
                }
            }
        },
    });
    return {
        updateGuide: mutate,
        updateGuideAsync: mutateAsync,
        ...otherProps,
    };
}

type UpdateGuideProgressParameters = Omit<UpdateGuideParameters, 'request'> & {
    progressDurationMs: number;
    eventType: MediaEvent;
};
export function useUpdateGuideProgress() {
    const { updateGuideAsync } = useUpdateGuide();

    const { mutate, mutateAsync, ...otherProps } = useMutation({
        mutationFn: async ({
            workflowDefnId,
            workflowType,
            programId,
            guideId,
            packClass,
            progressDurationMs,
            eventType,
        }: UpdateGuideProgressParameters) =>
            updateGuideAsync({
                programId,
                guideId,
                workflowDefnId,
                workflowType,
                packClass,
                request: {
                    progressDurationMs: Math.floor(progressDurationMs),
                    timestamp: getLocalDateTimeString(),
                    progressAction: getProgressAction(eventType),
                },
            }),
    });

    return {
        updateGuideProgress: mutate,
        updateGuideProgressAsync: mutateAsync,
        ...otherProps,
    };
}
